import { HttpClient } from '@angular/common/http';
import { jwtDecode } from 'jwt-decode';
import { Injectable, inject } from '@angular/core';
import { FronteggAuthService } from '@frontegg/angular';
import { BehaviorSubject, Observable, filter, take, firstValueFrom } from 'rxjs';

// Define the interface that matches the decoded token structure
export interface DecodedToken {
  tenantId: string;
  name: string;
  exp?: number;
  customClaims: {
    principal_id: string;
  };
}

export interface AuthPayload {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private fronteggAuthService = inject(FronteggAuthService);

  private authToken$ = new BehaviorSubject<string | null>(null);
  private isInitialized$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.initializeTokenSync();
  }

  /**
   * Initializes continuous token synchronization with Frontegg
   * Automatically updates token when Frontegg refreshes it
   */
  private initializeTokenSync(): void {
    const storedToken = sessionStorage.getItem('authToken');

    if (storedToken && this.isTokenValid(storedToken)) {
      this.authToken$.next(storedToken);
      this.isInitialized$.next(true);
    }

    this.fronteggAuthService.authState$.subscribe(authState => {
      if (authState.isAuthenticated && authState.user?.accessToken) {
        const token = authState.user.accessToken;
        this.authToken$.next(token);
        sessionStorage.setItem('authToken', token);
        this.isInitialized$.next(true);
      } else {
        this.clearToken();
      }
    });
  }

  /**
   * Validates if a token is still valid (not expired)
   * @param token JWT token to validate
   * @returns true if token is valid, false otherwise
   */
  private isTokenValid(token: string): boolean {
    try {
      const decoded: DecodedToken = jwtDecode<DecodedToken>(token);

      if (decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        return decoded.exp > currentTime + 60;
      }

      return false;
    } catch (error) {
      console.error('Invalid token format:', error);
      return false;
    }
  }

  /**
   * Extracts tenantId from the decoded JWT token
   * @param authToken user's auth token
   * @returns tenantId as string
   */
  extractTenantId(authToken: string): string {
    const decodedToken: DecodedToken = jwtDecode<DecodedToken>(authToken);
    return decodedToken.tenantId;
  }

  /**
   * Extracts user info from the decoded JWT token
   * @param authToken user's auth token
   * @returns object containing key, name, and tenantId
   */
  extractUserInfo(authToken: string): { key: string; name: string; tenantId: string } {
    const decodedToken: DecodedToken = jwtDecode<DecodedToken>(authToken);

    const key = decodedToken.customClaims.principal_id;
    const name = decodedToken.name;
    const tenantId = decodedToken.tenantId;

    return { key, name, tenantId };
  }

  /**
   * Sets the auth token
   * @param token a bearer token
   */
  setAuthToken(token: string): void {
    this.authToken$.next(token);
    sessionStorage.setItem('authToken', token);
    this.isInitialized$.next(true);
  }

  /**
   * Returns the auth token (synchronous)
   * @returns the auth token or null
   */
  getAuthToken(): string | null {
    const token = this.authToken$.value || sessionStorage.getItem('authToken');

    if (token && !this.isTokenValid(token)) {
      this.clearToken();
      return null;
    }

    return token;
  }

  /**
   * Returns the auth token as an Observable
   * @returns Observable of the auth token
   */
  getAuthToken$(): Observable<string | null> {
    return this.authToken$.asObservable();
  }

  /**
   * Waits for authentication to complete and returns the token
   * Use this in components that load on page refresh
   * @returns Promise that resolves with the auth token
   */
  async waitForToken(): Promise<string | null> {
    const currentToken = this.getAuthToken();
    if (currentToken && this.isInitialized$.value) {
      return currentToken;
    }

    try {
      await firstValueFrom(
        this.isInitialized$.pipe(
          filter(initialized => initialized),
          take(1)
        )
      );

      return this.getAuthToken();
    } catch (error) {
      console.error('Timeout waiting for authentication');
      return null;
    }
  }

  /**
   * Clears the auth token from memory and storage
   */
  private clearToken(): void {
    this.authToken$.next(null);
    sessionStorage.removeItem('authToken');
    this.isInitialized$.next(false);
  }
}
